import { CopyOutlined, EllipsisOutlined } from '@ant-design/icons'
import { useApolloClient, useMutation } from '@apollo/client'
import { faColumns3 } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CopyViewDocument, FetchViewQueryResultAsCsvDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath, POSTHOG_EVENTS, posthogTrackClick } from '@salescore/client-base'
import { useCanForView, useMe } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type ViewQueryList,
} from '@salescore/core'
import { ButtonWithTooltip, CsvDownloadWithTextButton } from '@salescore/frontend-common'
import { Button, Dropdown, message, Space } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { useGetQueryWithFilterTree } from '../../../hooks/useGetQueryWithFilterTree'
import { useMustacheParameter } from '../../../hooks/useMustacheParameter'
import { viewsContextAtom } from '../../../recoil/view/atoms'
import { ColumnsDrawer } from './SheetNavigation/ColumnsDrawer'

export const SheetMoreActionsButton = ({
  compiledConfig,
  view,
  query,
  setHasChangeToViewSchema,
}: {
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const client = useApolloClient()
  const me = useMe()
  const organizationId = me.organization.id
  const getQueryWithFilterTree = useGetQueryWithFilterTree(query)
  const mustacheParameter = useMustacheParameter()
  const canCreate = useCanForView('create', view)
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const canDownloadCsv = useCanForView('download-csv', view)

  const [isDrawerOpen, setDrawerOpen] = useState(false)

  const [copy] = useMutation(CopyViewDocument)
  const { refetchViews, pickView, isSheetMoreActionsCopyVisible } = useRecoilValue(viewsContextAtom)

  return (
    <>
      <Dropdown
        // eslint-disable-next-line sonarjs/deprecation
        overlay={
          <Space className="bg-white p-3 shadow-lg" direction="vertical">
            <ButtonWithTooltip
              disabled={!canDisplayConfigEdit}
              showTooltip={!canDisplayConfigEdit}
              tooltipTitle={t(`権限がないため、表示できません。`)}
              className="w-full justify-start"
              icon={<FontAwesomeIcon icon={faColumns3} color="black" height={16} width={16} />}
              type="text"
              onClick={() => {
                setDrawerOpen(true)
              }}
            >
              {t(`カラム`)}
            </ButtonWithTooltip>
            {isSheetMoreActionsCopyVisible !== false && (
              <ButtonWithTooltip
                className="w-full justify-start"
                disabled={!canCreate || view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX)}
                showTooltip={!canCreate}
                tooltipTitle={t(`権限がないため、複製はできません。`)}
                type="text"
                icon={<CopyOutlined />}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
                onClick={async () => {
                  try {
                    const { data: result } = await copy({
                      variables: {
                        organizationId: getOrganizationIdFromPath(),
                        id: view.id,
                      },
                    })
                    const savedView = result?.copyView
                    if (savedView === undefined) {
                      void message.warning(t(`エラーが発生しました`))
                    } else {
                      void message.success(t(`「{{name}}」を作成しました。`, { name: savedView.name }))
                      await refetchViews()
                      pickView(savedView.id)
                    }
                  } catch (error) {
                    if (error instanceof Error) {
                      void message.error(`${t(`エラーが発生しました。`)}${error.message}`)
                    } else {
                      void message.error(t(`エラーが発生しました`))
                    }
                  }
                }}
              >
                {t(`複製`)}
              </ButtonWithTooltip>
            )}
            {
              <CsvDownloadWithTextButton
                filename={t(`シート`)}
                text={t(`CSVダウンロード`)}
                type="text"
                // 2023/06 いったん個社対応
                disabled={!canDownloadCsv}
                onClick={() => {
                  posthogTrackClick(POSTHOG_EVENTS.click_sheet_csv_download, view.id)
                }}
                fetch={async () => {
                  const viewQuery = getQueryWithFilterTree()
                  if (viewQuery === undefined) {
                    // sheet view 専用 navigation (SheetNavigation) 配下のため kpi/kpiPivot
                    // view では本 button が render されない (型上の保険)。
                    throw new Error(``)
                  }
                  const { data: result } = await client.query({
                    query: FetchViewQueryResultAsCsvDocument,
                    variables: {
                      organizationId,
                      viewQuery,
                      mustacheParameter,
                      viewUi: compiledConfig.ui,
                      viewId: view.id,
                    },
                  })
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
                  if (result.viewResultAsCsv === undefined) {
                    throw new Error(``)
                  }
                  if (isNotNullish(result.viewResultAsCsv.error) && result.viewResultAsCsv.error !== '') {
                    void message.error(result.viewResultAsCsv.error)
                    throw new Error(``)
                  }
                  if (result.viewResultAsCsv.hasNext) {
                    void message.warning(t(`行が多すぎるため、全ての行を取得できませんでした`))
                  }
                  return result.viewResultAsCsv.csv
                }}
              />
            }
          </Space>
        }
        // okText="実行"
        // cancelText="キャンセル"
      >
        <Button icon={<EllipsisOutlined />} type="text" />
      </Dropdown>
      <ColumnsDrawer
        isOpen={isDrawerOpen}
        hide={() => {
          setDrawerOpen(false)
        }}
        compiledConfig={compiledConfig}
        query={query}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
    </>
  )
}
